import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { vipAPI } from '../../../services/api';
import { useBrand } from '../../../context/BrandContext';
import { useAgent } from '../../../context/AgentContext';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../ui/select';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Spinner } from '../../ui/spinner';
import { useToast } from '../../ui/toast';
import AnniversaryBonusRow, { COLUMN_COUNT } from './AnniversaryBonusRow';
import { formatCents, STATUS_OPTIONS, STATUS_BADGE, todayISO, nextSort, playerSearchParams } from './format';
import { PlayerSearch } from './ModerationEditor';
import { usePlayerParam } from './playerUrl';
import { SortableHead } from '../../game_department/TableControls';

// Radix Select cannot hold an empty value, so "all" stands for "no filter".
const ALL = 'all';

const initialFilters = () => ({ from: todayISO(), to: '', status: ALL, anniversaryType: ALL, bonusType: ALL, vipAgent: ALL, sortCol: '', sortDir: '' });

const toParams = (f) => {
  const p = {};
  if (f.from) p.from = f.from;
  if (f.to) p.to = f.to;
  if (f.status !== ALL) p.status = f.status;
  if (f.anniversaryType !== ALL) p.anniversary_type = f.anniversaryType;
  if (f.bonusType !== ALL) p.bonus_type = f.bonusType;
  if (f.vipAgent !== ALL) p.vip_agent = f.vipAgent;
  Object.assign(p, playerSearchParams(f.player));
  if (f.sortCol) { p.sort_col = f.sortCol; p.sort_dir = f.sortDir; }
  return p;
};

function FilterSelect({ label, value, onChange, options, width = 'w-36' }) {
  return (
    <label className="flex flex-col gap-1 text-xs font-medium text-ink-dim">
      {label}
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={`h-9 ${width} text-sm`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All</SelectItem>
          {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </label>
  );
}

function Summary({ items }) {
  const byStatus = useMemo(() => {
    const acc = {};
    items.forEach((r) => {
      const k = r.status || 'unknown';
      acc[k] = acc[k] || { count: 0, total: 0 };
      acc[k].count += 1;
      acc[k].total += r.effective_amount_usd_cents || 0;
    });
    return acc;
  }, [items]);
  // "To issue" leaves out bonuses already paid out; scheduled ones are booked but not yet issued.
  const total = items.reduce((s, r) => (r.status === 'issued' ? s : s + (r.effective_amount_usd_cents || 0)), 0);

  return (
    <div className="flex flex-wrap gap-2 text-xs font-medium">
      <span className="rounded-md bg-surface-2 px-2.5 py-1 text-ink">
        {items.length} proposals · <span className="font-bold">{formatCents(total)}</span> to issue
      </span>
      {Object.entries(byStatus).map(([status, { count, total: t }]) => (
        <span key={status} className="rounded-md bg-surface-2 px-2.5 py-1 text-ink-dim">
          {STATUS_BADGE[status]?.label || status}: {count} · {formatCents(t)}
        </span>
      ))}
    </div>
  );
}

export default function AnniversaryBonusesPage() {
  const { activeBrand } = useBrand();
  const { agents } = useAgent();
  const { show, ToastComponent } = useToast();
  const [filters, setFilters] = useState(initialFilters);
  const [player, setPlayer] = usePlayerParam();
  const [data, setData] = useState({ items: [], truncated: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const params = useMemo(() => toParams({ ...filters, player }), [filters, player]);

  // Only the latest request may update state, so a slow response for earlier filters, sorts or
  // brand cannot overwrite the current one.
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const isLatest = () => requestId === requestIdRef.current;
    setLoading(true);
    setError('');
    try {
      const res = await vipAPI.getAnniversaryBonuses(params);
      if (isLatest()) setData({ items: res.data.items || [], truncated: !!res.data.truncated });
    } catch (e) {
      if (isLatest()) setError(e?.response?.data?.error || 'Failed to load anniversary bonuses.');
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load, activeBrand]);

  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }));
  const onSort = (col) => setFilters((f) => ({ ...f, ...nextSort(f, col) }));
  const sortProps = { sortCol: filters.sortCol, sortDir: filters.sortDir, onSort };

  const moderate = async (row, body) => {
    setBusyId(row.moderation_id);
    try {
      const res = await vipAPI.moderateAnniversaryBonus(row.moderation_id, body);
      // Moderation changes only the status among the filtered fields: drop the row when its new
      // status no longer matches the status filter (e.g. approved under "Pending").
      const stillMatches = filters.status === ALL || res.data.status === filters.status;
      setData((d) => ({
        ...d,
        items: stillMatches
          ? d.items.map((r) => (r.moderation_id === row.moderation_id ? res.data : r))
          : d.items.filter((r) => r.moderation_id !== row.moderation_id),
      }));
      setEditingId(null);
      show(`Bonus for player ${row.user_id} ${res.data.status}.`, 'success');
    } catch (e) {
      const status = e?.response?.status;
      show(e?.response?.data?.error || 'Saving failed.', 'error');
      if (status === 409) {
        setEditingId(null);
        load();
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-ink">Anniversary Bonuses</h1>
        <p className="text-xs font-medium text-ink-dim">
          Bonuses proposed 7 days before a player's birthday (or sign-up anniversary when the birthday is unknown).
          Pending and approved bonuses are issued at the proposed amount. Changes close 1 day before the anniversary.
        </p>
      </div>

      <Card className="flex flex-wrap items-end gap-3 p-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-ink-dim">
          Anniversary from
          <Input type="date" value={filters.from} onChange={(e) => setFilter('from')(e.target.value)} className="h-9 w-40 text-sm" />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-ink-dim">
          to
          <Input type="date" value={filters.to} onChange={(e) => setFilter('to')(e.target.value)} className="h-9 w-40 text-sm" />
        </label>
        <FilterSelect label="Status" value={filters.status} onChange={setFilter('status')}
          options={STATUS_OPTIONS.map((s) => [s, STATUS_BADGE[s].label])} />
        <FilterSelect label="Anniversary" value={filters.anniversaryType} onChange={setFilter('anniversaryType')}
          options={[['birthday', 'Birthday'], ['signup', 'Sign-up']]} />
        <FilterSelect label="Player type" value={filters.bonusType} onChange={setFilter('bonusType')}
          options={[['vip', 'VIP'], ['regular', 'Regular']]} />
        <FilterSelect label="VIP manager" value={filters.vipAgent} onChange={setFilter('vipAgent')} width="w-44"
          options={agents.map((a) => [a, a])} />
        <PlayerSearch value={player} onChange={setPlayer} />
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => { setFilters(initialFilters()); setPlayer(''); }}>Upcoming</Button>
          <Button variant="ghost" size="sm" onClick={() => setFilters((f) => ({ ...f, from: '', to: '' }))}>All dates</Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      </Card>

      {!loading && !error && <Summary items={data.items} />}
      {data.truncated && (
        <p className="text-xs font-medium text-warning">Showing the first {data.items.length} rows. Narrow the filters to see the rest.</p>
      )}
      {error && <p className="text-xs font-medium text-negative">{error}</p>}

      <Card className="overflow-x-auto p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Player</TableHead>
              <SortableHead col="level" label="Level" {...sortProps} />
              <TableHead>Type</TableHead>
              <TableHead>Anniversary</TableHead>
              <SortableHead col="date" label="Date" {...sortProps} />
              <TableHead className="text-right">Days</TableHead>
              <SortableHead col="ngr" label="NGR 1y" right {...sortProps} />
              <SortableHead col="proposed" label="Proposed" right {...sortProps} />
              <SortableHead col="override" label="Override" right {...sortProps} />
              <TableHead>Status</TableHead>
              <TableHead>Moderated</TableHead>
              <TableHead>Note</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={COLUMN_COUNT} className="py-10 text-center"><Spinner /></TableCell></TableRow>
            ) : data.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="py-10 text-center text-sm font-medium text-ink-dim">
                  No anniversary bonuses match these filters.
                </TableCell>
              </TableRow>
            ) : (
              data.items.map((row) => (
                <AnniversaryBonusRow
                  key={row.moderation_id}
                  row={row}
                  editing={editingId === row.moderation_id}
                  busy={busyId === row.moderation_id}
                  onEdit={() => setEditingId(row.moderation_id)}
                  onClose={() => setEditingId(null)}
                  onModerate={moderate}
                />
              ))
            )}
          </TableBody>
        </Table>
      </Card>
      {ToastComponent}
    </div>
  );
}
